


import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosNotifications, axiosRDV } from "../../api/axios";
import { NOTIFICATIONS_PATHS } from "../../api/endpoints";
import { selectEffectiveUserId } from "../../sous-profile/redux/selectors/sousProfileSelectors";

const normError = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.response?.data ||
  err?.message ||
  "Notification error";

const normalizeId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  s = s.trim();
  if (!s || ["undefined", "null", "nan"].includes(s.toLowerCase())) return null;
  return s;
};

/**
 * ✅ extract the real auth/user id
 * Priority:
 * 1) auth/user ids
 * 2) nested user ids
 * 3) pgId/uuid
 * 4) doctorId only as last fallback
 */
const pickBestUserId = (u) => {
  if (!u) return null;

  const nestedUser = u?.user || u?.raw?.user || null;

  const candidates = [
    // direct auth/user ids
    u?.userId,
    u?.authUserId,
    u?.accountId,
    u?.sub,
    u?.id, // sometimes valid, but checked after userId candidates in effect order
    u?.pgId,
    u?.uuid,

    // nested user object
    nestedUser?.userId,
    nestedUser?.authUserId,
    nestedUser?.accountId,
    nestedUser?.sub,
    nestedUser?.id,
    nestedUser?.pgId,
    nestedUser?.uuid,

    // fallback doctor-specific
    u?.doctorUserId,
    nestedUser?.doctorUserId,

    // last fallback only
    u?.doctorId,
  ];

  for (const c of candidates) {
    const v = normalizeId(c);
    if (v) return v;
  }

  return null;
};

/** ✅ helper: userId depuis Redux (doctor + patient) */
const selectUserId = (state) => {
  const doctorCandidates = [
    state?.doctor?.user,
    state?.doctor?.currentUser,
    state?.doctor?.profile,
  ].filter(Boolean);

  for (const d of doctorCandidates) {
    const id = pickBestUserId(d);
    if (id) {
      return id;
    }
  }

  const effectivePatientId = selectEffectiveUserId(state);
  if (effectivePatientId) {
    return String(effectivePatientId);
  }

  const patientCandidates = [
    state?.patient?.user,
    state?.auth?.user,
    state?.user?.currentUser,
  ].filter(Boolean);

  for (const p of patientCandidates) {
    const id = pickBestUserId(p);
    if (id) {
      return id;
    }
  }

  return null;
};

/** ✅ helper: normalize payload from gateway/ms */
const unwrapPayload = (data) => {
  if (data && typeof data === "object" && data.data != null) return data.data;
  return data;
};

/**
 * GET notifications (paginated)
 * args: { skip=0, limit=20, unreadOnly?: boolean }
 */
export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async (
    { skip = 0, limit = 20, unreadOnly, cursor, since, userId: forcedUserId } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const userId = normalizeId(forcedUserId) || selectUserId(state);

      if (!userId) {
        return rejectWithValue("Missing userId in store (load current user first)");
      }


      const res = await axiosNotifications.get(
        NOTIFICATIONS_PATHS.LIST(userId, skip, limit, unreadOnly, cursor, since),
        { withCredentials: true }
      );

      const raw = res?.data;
      const payload = unwrapPayload(raw);

      if (Array.isArray(payload)) {
        const unreadCount = payload.reduce((n, x) => n + (x?.read ? 0 : 1), 0);
        return { items: payload, total: payload.length, unreadCount, skip, limit, nextCursor: null, hasMore: false, userId };
      }

      const items =
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.data) && payload.data) ||
        (Array.isArray(payload?.notifications) && payload.notifications) ||
        (Array.isArray(payload?.results) && payload.results) ||
        (Array.isArray(payload?.items?.items) && payload.items.items) ||
        [];

      const total =
        (Number.isFinite(payload?.total) && payload.total) ||
        (Number.isFinite(payload?.meta?.total) && payload.meta.total) ||
        (Number.isFinite(payload?.items?.total) && payload.items.total) ||
        items.length;

      let unreadCount =
        (Number.isFinite(payload?.unreadCount) && payload.unreadCount) ||
        (Number.isFinite(payload?.meta?.unreadCount) && payload.meta.unreadCount) ||
        (Number.isFinite(payload?.items?.unreadCount) && payload.items.unreadCount) ||
        undefined;

      if (!Number.isFinite(unreadCount)) {
        unreadCount = items.reduce((n, x) => n + (x?.read ? 0 : 1), 0);
      }

      return { items, total, unreadCount, skip, limit, cursor, nextCursor: payload?.nextCursor || null, hasMore: !!payload?.hasMore, userId };
    } catch (err) {
      return rejectWithValue(normError(err));
    }
  }
);

/**
 * PATCH read one
 * args: { id }
 */
export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async ({ id, userId: forcedUserId }, { rejectWithValue, getState }) => {
    try {
      const userId = normalizeId(forcedUserId) || selectUserId(getState());
      if (!userId) return rejectWithValue("Missing userId in store");

      const res = await axiosNotifications.patch(
        NOTIFICATIONS_PATHS.READ_ONE(userId, id),
        {},
        { withCredentials: true }
      );

      const raw = res?.data;
      const payload = unwrapPayload(raw);

      return payload?.notification || payload || { id, read: true };
    } catch (err) {
      return rejectWithValue(normError(err));
    }
  }
);

/**
 * PATCH read all
 */
export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllRead",
  async ({ userId: forcedUserId } = {}, { rejectWithValue, getState }) => {
    try {
      const userId = normalizeId(forcedUserId) || selectUserId(getState());
      if (!userId) return rejectWithValue("Missing userId in store");

      const res = await axiosNotifications.patch(
        NOTIFICATIONS_PATHS.READ_ALL(userId),
        {},
        { withCredentials: true }
      );

      const raw = res?.data;
      const payload = unwrapPayload(raw);

      return payload || { ok: true };
    } catch (err) {
      return rejectWithValue(normError(err));
    }
  }
);

/**
 * POST respond to an access request
 * args: { requestId, status, scopes?, validUntil? }
 */
export const respondAccessRequest = createAsyncThunk(
  "notifications/respondAccessRequest",
  async ({ requestId, status, scopes, validUntil, subprofileId }, { rejectWithValue }) => {
    try {
      if (!requestId) return rejectWithValue("Missing requestId");
      if (!status) return rejectWithValue("Missing status");

      const url = `/access-requests/${encodeURIComponent(requestId)}/respond`;
      const res = await axiosRDV.post(
        url,
        {
          status,
          ...(status === "accepted" && Array.isArray(scopes) ? { scopes } : {}),
          ...(status === "accepted" && validUntil ? { validUntil } : {}),
          ...(subprofileId ? { subprofileId } : {}),
        },
        { withCredentials: true },
      );

      return res?.data;
    } catch (err) {
      const errorMessage = normError(err);
      if (
        err?.response?.status === 409 &&
        String(errorMessage).toLowerCase().includes("already been resolved")
      ) {
        return { id: requestId, status: "resolved", alreadyResolved: true };
      }
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * POST revoke an accepted medical access request.
 * Patient identity is derived and verified by the gateway; it must not be
 * accepted from the browser payload.
 */
export const revokeAccessRequest = createAsyncThunk(
  "notifications/revokeAccessRequest",
  async ({ requestId, subprofileId = null }, { rejectWithValue }) => {
    try {
      if (!requestId) return rejectWithValue("Missing requestId");

      const url = `/access-requests/${encodeURIComponent(requestId)}/revoke`;
      const res = await axiosRDV.post(
        url,
        { subprofileId },
        { withCredentials: true },
      );
      return res?.data;
    } catch (err) {
      const errorMessage = normError(err);
      if (err?.response?.status === 409) {
        return {
          id: requestId,
          status: "resolved",
          alreadyResolved: true,
          message: errorMessage,
        };
      }
      return rejectWithValue(errorMessage);
    }
  }
);
